/* eslint-disable @typescript-eslint/require-array-sort-compare */
import { Divider } from 'antd';
import dayjs from 'dayjs';
import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import { useGetCurrencySymbol } from 'app/appState';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import {
  Contract,
  UnitType,
  generateContractPdfLink,
  AutochargeStatus,
  useUpdateContractMutation,
  useUpdateContractAccessMutation,
  ContractUnitType,
} from 'entities/Contract';
import { ReactComponent as InfoIcon } from 'shared/assets/icons/InfoIcon.svg';
import { Invoice, InvoiceFrequencyType } from 'entities/Invoice';
import { BulletsTable, TableRow } from 'shared/ui/BulletsTable';
import { Button } from 'shared/ui/Button';
import { getClientDateFormat, getServerDateWithMinuteTimeFormat } from 'shared/utils/helpers/getDateFormat';
import { Checkbox } from 'shared/ui/Checkbox';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { CloseContract } from 'features/CloseContract';
import { isCloseContractBlocked } from 'features/CloseContract/utils/getCloseContractRestrictions';
import { getUserName, useRole, UserPermissions } from 'entities/User';
import { NotifyLeaveDate } from 'features/NotifyLeaveDate';
import { useGetVatRateQuery } from 'entities/VatRate';
import { calculateDiscountValues } from 'entities/Discount';
import { getBoxRentalPriceAmount } from 'entities/Box';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';
import { AutochargeSettings } from 'features/AutochargeSettings';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from 'app/config/routerConfig/types';
import { Paragraph } from 'shared/ui/Paragraph';
import { ContractClosureRequest } from 'widgets/ContractClosureRequest';
import { ChangeContractApplicable } from 'features/ChangeContractApplicable';
import { Toggle } from 'shared/ui/Toggle';
import { showNotification } from 'app/providers/NotificationsProvider';
import { SendContractByEmail } from 'features/SendContractByEmail';
import { useCheckPermission } from 'shared/utils/hooks/useCheckPermission';
import { getSizeCodeTypeString } from 'entities/SizeCode';
import { useGetContractVerificationDocumentsQuery } from 'entities/VerificationPhoto';
import { MoveOutPhotosModal } from 'widgets/MoveOutPhotosModal';
import { BoxHistoryActionNames, useGetPaginatedHistoryQuery } from 'entities/History';

interface ContractDetailsProps {
  contract: Contract;
  invoices: Invoice[];
  closeContractDetails: () => void;
}

export const ContractDetails: FC<ContractDetailsProps> = memo((props) => {
  const {
    contract: {
      contractItems,
      contractNumber,
      user,
      startDate,
      endDate,
      initialEndDate,
      invoiceFrequencyType,
      contractId,
      leaveDate,
      noticeLeaveDate,
      autocharges,
      deposit,
      warehouse,
      isPrePaymentApplied,
      description,
      isApplicable,
      isAutoClosingIgnored,
      isLateChargeIgnored,
      accessDenied,
      isContractSigned,
      closedBy,
      createdBy,
      closingWithoutVerificationReason,
      createdAt,
    },
    closeContractDetails,
  } = props;

  const { t } = useAppTranslation('contracts');

  const navigate = useNavigate();

  const [updateContract] = useUpdateContractMutation();
  const [updateContractAccess] = useUpdateContractAccessMutation();

  const { data: moveOutDocs = [] } = useGetContractVerificationDocumentsQuery({ contractId }, { skip: !contractId });

  const rentData = contractItems?.find((item) => [ContractUnitType.BOX, ContractUnitType.OFFICE].includes(item.contractUnitType));
  const insuranceData = contractItems?.find((item) => item.contractUnitType === ContractUnitType.INSURANCE);

  const { data: lastBoxOpenings } = useGetPaginatedHistoryQuery(
    {
      boxId: rentData?.box?.originalEntityId,
      orderBy: 'createdAt:DESC',
      limit: 2,
      actionName: [BoxHistoryActionNames.box_open, BoxHistoryActionNames.box_open_attempt],
      startDate: getServerDateWithMinuteTimeFormat(startDate),
      endDate: leaveDate ? getServerDateWithMinuteTimeFormat(dayjs(leaveDate).endOf('day')) : undefined,
    },
    { skip: !rentData?.box?.originalEntityId },
  );

  const [isMoveOutPhotosOpen, setMoveOutPhotosOpen] = useState(false);

  const currencySymbol = useGetCurrencySymbol();
  const { isManagement, isPartner } = useRole();

  const isForbiddenManageLateCharge = useCheckPermission(UserPermissions.MANAGE_LATE_CHARGE);
  const isForbiddenManageAutoclosing = useCheckPermission(UserPermissions.MANAGE_CONTRACT_AUTOCLOSING);
  const isForbiddenManageAccess = useCheckPermission(UserPermissions.MANAGE_CONTRACT_ACCESS);

  const boxVatRateParams = {
    accountType: user.accountType,
    unitType: UnitType.BOX,
  };

  const insuranceVatRateParams = {
    accountType: user.accountType,
    unitType: UnitType.INSURANCE,
  };

  const { data: boxVatRate } = useGetVatRateQuery(boxVatRateParams);
  const { data: insuranceVatRate } = useGetVatRateQuery(insuranceVatRateParams);

  const contractDuration = dayjs(endDate).diff(startDate, invoiceFrequencyType);
  const minRentalPeriod = dayjs(initialEndDate).diff(startDate, invoiceFrequencyType);

  const priceDetailsBullets = useMemo(() => {
    const tableRows: TableRow[] = [];

    const insuranceAmount: Record<InvoiceFrequencyType, number | null> = {
      [InvoiceFrequencyType.DAY]: insuranceData?.insurance?.dailyRate ?? null,
      [InvoiceFrequencyType.WEEK]: insuranceData?.insurance?.weekRate ?? null,
      [InvoiceFrequencyType.MONTH]: insuranceData?.insurance?.monthRate ?? null,
    };

    const insuranceVatPercent = insuranceVatRate?.ratePercent || 0;
    const boxVatPercent = boxVatRate?.ratePercent || 0;
    const insuranceBaseRate = insuranceAmount[invoiceFrequencyType] as number;
    const contractDurationPeriodNumbers = Array.from({ length: contractDuration }, (el, index) => index + 1);
    const rentalPrice = getBoxRentalPriceAmount(rentData?.box, invoiceFrequencyType);

    if (rentData?.box && rentalPrice) {
      const { contractPeriodsUnitPrices } = calculateDiscountValues(
        rentalPrice,
        rentData.box.discounts,
        contractDuration,
        contractDurationPeriodNumbers,
      );

      tableRows.push({
        label: t('Base box rent price'),
        value: `${rentalPrice} ${currencySymbol}/${t(invoiceFrequencyType)}`,
      });

      tableRows.push({
        label: t('Box rent prices'),
        highlighted: true,
        value: '',
      });

      const groupedPrices = contractPeriodsUnitPrices.reduce((acc: Record<number, number[]>, priceobject) => {
        if (acc[priceobject.rate]) {
          acc[priceobject.rate].push(priceobject.periodNumber);
        } else {
          acc[priceobject.rate] = [priceobject.periodNumber];
        }
        return acc;
      }, {});

      Object.entries(groupedPrices).forEach(([key, periodNumbers]) => {
        const currentPeriodUnitPriceWithoutVat = roundNumber(Number(key));

        if (periodNumbers.length === 1) {
          tableRows.push({
            label: t('{{periodNumber}} {{invoiceFrequencyType}} price', {
              periodNumber: periodNumbers[0],
              invoiceFrequencyType: t(invoiceFrequencyType),
            }),
            value: `${currentPeriodUnitPriceWithoutVat} ${currencySymbol}`,
          });
        } else {
          tableRows.push({
            label: t('{{startPeriodNumber}} - {{endPeriodNumber}} {{invoiceFrequencyType}} price', {
              startPeriodNumber: periodNumbers.sort((a, b) => a - b)[0],
              endPeriodNumber: periodNumbers.sort((a, b) => a - b)[periodNumbers.length - 1],
              invoiceFrequencyType: t(`${invoiceFrequencyType}s`),
            }),
            value: `${currentPeriodUnitPriceWithoutVat} ${currencySymbol}`,
          });
        }
      });

      /* tableRows.push({
        label: t('Rental price from {{contractDuration}} {{invoiceFrequencyType}}', {
          contractDuration: contractDuration + 1,
          invoiceFrequencyType: t(invoiceFrequencyType),
        }),
        value: `${rentalPrice} ${currencySymbol}/${t(invoiceFrequencyType)}`,
      }); */
    }

    if (boxVatPercent > 0) {
      tableRows.push({
        label: t('VAT'),
        value: `${boxVatPercent}%`,
      });
    }

    tableRows.push({
      label: t('Insurance rate', { insuranceVatPercent }),
      value: `${roundNumber(insuranceBaseRate)} ${currencySymbol}/${t(invoiceFrequencyType)}`,
    });

    if (insuranceVatPercent > 0) {
      tableRows.push({
        label: t('Insurance VAT'),
        value: `${insuranceVatPercent}%`,
      });
    }

    tableRows.push({
      label: t('Insurance coverage amount'),
      value: insuranceData?.insurance?.coverageAmount ? `${roundNumber(insuranceData.insurance.coverageAmount)} ${currencySymbol}` : null,
      emptyValueSymbol: `0 ${currencySymbol}`,
    });

    if (boxVatPercent === 0 && insuranceVatPercent === 0) {
      tableRows.push({
        label: t('VAT'),
        value: '0%',
      });
    }

    return tableRows;
  }, [
    rentData,
    boxVatRate?.ratePercent,
    contractDuration,
    currencySymbol,
    insuranceData,
    insuranceVatRate?.ratePercent,
    invoiceFrequencyType,
    t,
  ]);

  const discountsBullets = useMemo(() => {
    const bullets = rentData?.box?.discounts.map((discount, index) => {
      return {
        label: `${index + 1}. ${getLocalizedString(discount.description)}`,
        value: '',
      };
    });

    return bullets || [];
  }, [rentData?.box?.discounts]);

  const generalInfoBullets = useMemo(() => {
    let bullets: TableRow[] = [
      { label: t('Rent type'), value: t(`${invoiceFrequencyType} rent`) },
      { label: t('Date of creation'), value: getClientDateFormat(createdAt) },
      { label: t('Start Date'), value: getClientDateFormat(startDate) },
    ];

    if (leaveDate) {
      bullets.push({ label: t('Close Date'), value: getClientDateFormat(leaveDate) });
    }

    let autochargesTextColor;

    switch (autocharges) {
      case AutochargeStatus.DISABLED:
        autochargesTextColor = '';
        break;
      case AutochargeStatus.ENABLED:
        autochargesTextColor = 'text-success';
        break;
      case AutochargeStatus.STOPPED:
        autochargesTextColor = 'text-warn';
        break;
      default:
        autochargesTextColor = '';
        break;
    }

    bullets = bullets.concat([
      {
        label: t('Min rental period'),
        value: `${minRentalPeriod} ${t(minRentalPeriod === 1 ? `${invoiceFrequencyType}` : `${invoiceFrequencyType}s`)}`,
      },
      { label: t('Autocharges'), value: <span className={classNames(autochargesTextColor)}>{t(autocharges)}</span> },
      { label: t('Warehouse'), value: warehouse.name },
      { label: t(`${t(`${rentData?.box?.unitType}`)} Number`), value: rentData?.box?.name },
      {
        label: t('Area'),
        value: `${rentData?.box?.square} ${getSizeCodeTypeString(rentData?.box?.sizeCode?.sizeCodeType)}`,
      },
    ]);

    return bullets;
  }, [
    t,
    invoiceFrequencyType,
    startDate,
    leaveDate,
    autocharges,
    minRentalPeriod,
    warehouse.name,
    rentData?.box?.unitType,
    rentData?.box?.name,
    rentData?.box?.square,
    rentData?.box?.sizeCode?.sizeCodeType,
  ]);

  const goToUserProfile = useCallback((): void => {
    closeContractDetails();
    navigate(`${AppRoutes.PROFILE}/${user.userId}`);
  }, [navigate, user, closeContractDetails]);

  const openMoveOutPhotosModal = useCallback((): void => {
    setMoveOutPhotosOpen(true);
  }, []);

  const closeMoveOutPhotosModal = useCallback((): void => {
    setMoveOutPhotosOpen(false);
  }, []);

  const paragraphs = warehouse.bankingRequisits?.split('\n');

  const paragraphElements = paragraphs?.map((paragraph: string, index: number) => (
    <p key={index} className="text-sm text-primaryLight !mb-0">
      {paragraph}
    </p>
  ));

  const handleToggleIgnoreAutoClosing = useCallback(async () => {
    try {
      await updateContract({ contractId, params: { isAutoClosingIgnored: !isAutoClosingIgnored } }).unwrap();
      showNotification('info', t('Success', { ns: 'common' }), t('Contract has been successfully changed'));
    } catch (error) {
      showNotification('error', t('Error', { ns: 'common' }), t('Error when changing contract'));
      console.log('error', error);
    }
  }, [contractId, isAutoClosingIgnored, t, updateContract]);

  const handleToggleIgnoreLateCharge = useCallback(async () => {
    try {
      await updateContract({ contractId, params: { isLateChargeIgnored: !isLateChargeIgnored } }).unwrap();
      showNotification('info', t('Success', { ns: 'common' }), t('Contract has been successfully changed'));
    } catch (error) {
      showNotification('error', t('Error', { ns: 'common' }), t('Error when changing contract'));
      console.log('error', error);
    }
  }, [contractId, isLateChargeIgnored, t, updateContract]);

  const handleToggleAccessDenied = useCallback(async () => {
    try {
      await updateContractAccess({ contractId, accessDenied: !accessDenied }).unwrap();
      showNotification('info', t('Success', { ns: 'common' }), t('Contract has been successfully changed'));
    } catch (error) {
      showNotification('error', t('Error', { ns: 'common' }), t('Error when changing contract'));
      console.log('error', error);
    }
  }, [contractId, accessDenied, t, updateContractAccess]);

  const toggleContractAgreement = useCallback(async (): Promise<void> => {
    try {
      await updateContract({ contractId, params: { isContractSigned: !isContractSigned } }).unwrap();
      showNotification('info', t('Success', { ns: 'common' }), t('Contract agreement has been changed', { ns: 'common' }));
    } catch (error: CustomAny) {
      showNotification('error', t('Error', { ns: 'common' }), t('Error when changing contract agreement', { ns: 'common' }));
    }
  }, [contractId, isContractSigned, updateContract, t]);

  const hasExtendedClosePermission = !useCheckPermission(UserPermissions.CLOSE_CONTRACT_EXTENDED);
  const isCloseBlocked = isCloseContractBlocked(moveOutDocs, hasExtendedClosePermission);
  const hasPhotoVerificationIssues = moveOutDocs.length > 0 && isCloseBlocked;

  const departureBullets = useMemo(() => {
    const departureLabels: TableRow[] = [];

    if (noticeLeaveDate) {
      departureLabels.push({
        label: t('Planned Departure Date'),
        value: getClientDateFormat(noticeLeaveDate),
      });
    }

    if (moveOutDocs.length > 0) {
      departureLabels.push({
        label: t('Move-out photos'),
        value: (
          <div className="flex gap-2">
            {hasPhotoVerificationIssues && <InfoIcon width={22} height={22} className="[&>path]:stroke-warn" />}
            {t('Show')}
          </div>
        ),
        onClick: openMoveOutPhotosModal,
      });
    }

    if (closingWithoutVerificationReason) {
      departureLabels.push({
        label: t('Closing without verification reason'),
        value: t(closingWithoutVerificationReason),
      });
    }

    return departureLabels;
  }, [closingWithoutVerificationReason, hasPhotoVerificationIssues, moveOutDocs.length, noticeLeaveDate, openMoveOutPhotosModal, t]);

  return (
    <>
      <div className="font-semibold text-3xl">{t('Contract details')}</div>
      <div className="flex flex-col  pt-4">
        <div className="flex justify-between font-semibold text-lg">
          {t('Contract')} #{contractNumber}
          <a
            href={generateContractPdfLink(contractId)}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-accent"
          >
            {t('Download PDF')}
          </a>
        </div>
        {(isManagement || isPartner) && (
          <div>
            {leaveDate ? (
              <div className="flex gap-1">
                <span className="text-error">{t('Closed by')}</span>
                <span className="text-error">{closedBy ? `${getUserName(closedBy)}` : t('System')}</span>
              </div>
            ) : (
              createdBy && (
                <div className="flex gap-1">
                  <span className="text-accent">{t('Created by')}</span>
                  <span className="text-accent">{getUserName(createdBy)}</span>
                </div>
              )
            )}
          </div>
        )}
      </div>
      {(isManagement || isPartner) && description && (
        <div className="text-normal text-primaryLight py-1">{getLocalizedString(JSON.stringify(description))}</div>
      )}
      {warehouse.bankingRequisits && (
        <div className="text-sm text-primaryLight pt-2">
          {t('Bank requisites:')}

          <Paragraph expandable={false} className="whitespace-normal">
            {paragraphElements}
          </Paragraph>
        </div>
      )}

      {isManagement && (
        <div className="flex justify-between items-center">
          <Checkbox className="my-2" name="termsAgreement" checked={isContractSigned} onChange={toggleContractAgreement}>
            {t('Client agreed to the contract')}
          </Checkbox>
          {!isPartner && (
            <SendContractByEmail
              contractId={contractId}
              action={<span className="text-sm text-accent cursor-pointer">{t('Send by Email')}</span>}
            />
          )}
        </div>
      )}

      <BulletsTable className="pt-8" heading={t('General Information')} theme="clear" rows={generalInfoBullets} />

      {(isManagement || isPartner) && (
        <>
          <BulletsTable
            className="pt-5"
            heading={t('Client Information')}
            theme="clear"
            rows={[
              { label: t('Client'), value: getUserName(user), onClick: goToUserProfile },
              { label: t('Phone'), value: user.phone },
              { label: t('Email'), value: user.email },
              {
                label: `${user.street} ${user.house} ${user.apartment || ''} ${user.city} ${user.zip} ${user.country} ${
                  user.company ? `${user.firstName} ${user.lastName}` : ''
                }`,
                value: '',
              },
            ]}
          />
          <Divider className="border-primary" />
        </>
      )}

      <BulletsTable heading={t('Price details')} labelClassName="max-w-[200px]" theme="clear" rows={priceDetailsBullets} />
      {discountsBullets.length > 0 && (
        <>
          <Divider className="border-primary" />
          <BulletsTable heading={t('Discounts')} theme="clear" rows={discountsBullets} />
        </>
      )}
      <Divider className="border-primary" />
      <BulletsTable
        heading={t('Other')}
        theme="clear"
        rows={[
          {
            label: t('Deposit'),
            value: `${roundNumber(deposit?.depositAmount)} ${currencySymbol}`,
            emptyValueSymbol: `0 ${currencySymbol}`,
          },
          { label: t('Prepayment'), value: t(isPrePaymentApplied ? 'Applied' : 'Not applied') },
        ]}
      />

      {isManagement && !leaveDate && (
        <>
          <Divider className="border-primary" />
          <BulletsTable
            heading={t('Exceptions')}
            theme="clear"
            rows={[
              {
                label: t('Ignore autoclosing'),
                value: (
                  <Toggle
                    checked={isAutoClosingIgnored}
                    onChange={handleToggleIgnoreAutoClosing}
                    isDisabled={isForbiddenManageAutoclosing}
                  />
                ),
              },
              {
                label: t('Ignore late charge'),
                value: (
                  <Toggle checked={isLateChargeIgnored} onChange={handleToggleIgnoreLateCharge} isDisabled={isForbiddenManageLateCharge} />
                ),
              },
              {
                label: t('Block access'),
                value: <Toggle checked={accessDenied} onChange={handleToggleAccessDenied} isDisabled={isForbiddenManageAccess} />,
              },
            ]}
          />
        </>
      )}

      {(moveOutDocs.length > 0 || noticeLeaveDate) && (
        <>
          <Divider className="border-primary" />
          <BulletsTable heading={t('Move-out')} theme="clear" rows={departureBullets} />
        </>
      )}

      {!leaveDate ? (
        <div className="pt-12 space-y-4">
          {[AutochargeStatus.ENABLED, AutochargeStatus.STOPPED].includes(autocharges) && (
            <AutochargeSettings contract={props.contract} action={<Button theme="secondary">{t('Autocharge settings')}</Button>} />
          )}
          {!isPartner && (
            <div className="flex flex-col gap-4">
              <NotifyLeaveDate
                contractId={contractId}
                invoiceFrequencyType={invoiceFrequencyType}
                initialEndDate={initialEndDate}
                noticeLeaveDate={noticeLeaveDate}
                action={<Button theme="secondary">{t(`${noticeLeaveDate ? 'Change end of Rent' : 'Notify about end of Rent'}`)}</Button>}
              />

              {noticeLeaveDate && <ContractClosureRequest contractId={contractId} action={<Button>{t('Upload Photo')}</Button>} />}
            </div>
          )}

          {isManagement && (
            <CloseContract
              contract={props.contract}
              action={<Button theme="danger">{t('Close contract')}</Button>}
              isCloseBlocked={isCloseBlocked}
              moveOutDocs={moveOutDocs}
            />
          )}
        </div>
      ) : (
        isManagement && (
          <div className="pt-12 space-y-4">
            <ChangeContractApplicable
              contract={props.contract}
              action={<Button theme="danger">{t(`Make contract ${isApplicable ? 'inapplicable' : 'applicable'}`)}</Button>}
            />
          </div>
        )
      )}

      <MoveOutPhotosModal
        isOpen={isMoveOutPhotosOpen}
        onClose={closeMoveOutPhotosModal}
        contractId={contractId}
        lastBoxOpening={lastBoxOpenings?.content?.[0]?.createdAt}
      />
    </>
  );
});
